import _ from 'lodash';

import { ArticlePage } from '../../model/components/ArticlePage';
import { PopText } from '../../model/components/PopText';
import { PopTextV2 } from '../../model/components/PopTextv2';
import { Text } from '../../model/components/Text';
import { PathContextProvider } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { ArrayField, ArrayFieldItem } from './ArrayField';
import { PopTextInput } from './PopTextInput';
import { PopTextV2Input } from './PopTextV2Input';
import { TextInput } from './TextInput';
import { EMPTY_TRANSLATION_TABLE, UnityColor } from '@lifeserver/shared';

import { useSelectedLanguage } from '../../../hooks/selectedLanugage';

import { isArticleValidationOptions, POST_VALIDATIONS } from '../validators';
import styles from './ArticlePageInput.module.scss';

import { getTranslation } from '../../translationUtils';

const DEFAULT_TEXT_COLOR: UnityColor = { r: 0, g: 0, b: 0, a: 1 };
const DEFAULT_TEXT: Text = {
    text: EMPTY_TRANSLATION_TABLE,
    textColor: DEFAULT_TEXT_COLOR,
    fontSize: 60,
};
const DEFAULT_POP_TEXT: PopText = {
    position: { x: 0, y: 0 },
    animated: true,
    rows: [
        {
            backgroundColor: { r: 0, g: 0, b: 0, a: 1 },
            textColor: { r: 1, g: 1, b: 1, a: 1 },
            text: EMPTY_TRANSLATION_TABLE,
            fontSize: 60,
        },
    ],
};
const DEFAULT_POP_TEXT_V2: PopTextV2 = {
    position: { x: 0, y: 0 },
    animated: true,
    backgroundColor: { r: 0, g: 0, b: 0, a: 1 },
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    text: EMPTY_TRANSLATION_TABLE,
    fontSize: 60,
};
type TextStyle = 'NoText' | 'RegularText' | 'PopText' | 'PopTextV2';

const inferTextStyle = (page: ArticlePage): TextStyle => {
    if (page.popTexts) {
        return 'PopText';
    } else if (page.popTextV2s) {
        return 'PopTextV2';
    } else if (page.header) {
        return 'RegularText';
    }

    return 'NoText';
};

export interface TextTypeInputProps {
    value: ArticlePage;
    onChange?: (value: ArticlePage) => void;
    pageIndex: number;
    validations:
        | (typeof POST_VALIDATIONS)[keyof typeof POST_VALIDATIONS]
        | undefined;
}

export const TextTypeInput = ({
    value,
    onChange,
    pageIndex,
    validations,
}: TextTypeInputProps) => {
    const textStyle = inferTextStyle(value);
    const { selectedLanguage } = useSelectedLanguage();
    const { localizationRecord } = usePortalPostEditor();

    const onFieldChange = <Field extends keyof ArticlePage>(
        field: Field,
        fieldValue: ArticlePage[Field],
    ) => {
        onChange?.({
            ...value,
            [field]: fieldValue,
        });
    };

    const onTextStyleChange = (newTextStyle: TextStyle) => {
        if (newTextStyle === 'NoText') {
            onChange?.({
                ...value,
                popTexts: undefined,
                popTextV2s: undefined,
                body: undefined,
                header: undefined,
            });
        }
        if (newTextStyle === 'PopText') {
            // PopText is the legacy way of setting up text and should not be used for new content.
            // Localization requested to keep this option available until the new localization platform
            // is ready, at which point existing content will be migrated off the legacy format.
            onChange?.({
                ...value,
                body: undefined,
                header: undefined,
                popTextV2s: undefined,
                popTexts: [_.cloneDeep(DEFAULT_POP_TEXT)],
            });
        }
        if (newTextStyle === 'RegularText') {
            onChange?.({
                ...value,
                popTexts: undefined,
                popTextV2s: undefined,
                header: _.cloneDeep(DEFAULT_TEXT),
                body: _.cloneDeep(DEFAULT_TEXT),
            });
        }
        if (newTextStyle === 'PopTextV2') {
            onChange?.({
                ...value,
                body: undefined,
                header: undefined,
                popTexts: undefined,
                popTextV2s: [_.cloneDeep(DEFAULT_POP_TEXT_V2)],
            });
        }
    };
    const headerText =
        getTranslation(
            localizationRecord ?? {},
            value.header?.text?.$key ?? '',
            selectedLanguage,
        ) ?? '';

    const bodyText =
        getTranslation(
            localizationRecord ?? {},
            value.body?.text?.$key ?? '',
            selectedLanguage,
        ) ?? '';

    const headerLength = headerText.length;
    const bodyLength = bodyText.length;
    const totalLength = headerLength + bodyLength;
    const maxTotalLength = isArticleValidationOptions(validations)
        ? validations.pages?.[pageIndex]?.totalMaxLength
        : undefined;

    const renderPopText = () => {
        return (
            <fieldset>
                <legend>Pop Texts</legend>
                <PathContextProvider segment="popTexts">
                    <ArrayField
                        items={value.popTexts ?? [DEFAULT_POP_TEXT]}
                        onChange={(value) => onFieldChange('popTexts', value)}
                        onAdd={() => {
                            const newPopTexts = value.popTexts
                                ? [...value.popTexts, DEFAULT_POP_TEXT]
                                : [DEFAULT_POP_TEXT];
                            onFieldChange('popTexts', newPopTexts);
                        }}
                    >
                        {(element) => (
                            <ArrayFieldItem key={element.index} {...element}>
                                <PathContextProvider segment={element.index}>
                                    <PopTextInput
                                        value={element.item}
                                        onChange={element.onChange}
                                    />
                                </PathContextProvider>
                            </ArrayFieldItem>
                        )}
                    </ArrayField>
                </PathContextProvider>
            </fieldset>
        );
    };

    const renderPopTextV2 = () => {
        return (
            <fieldset>
                <legend>Pop Texts v2</legend>
                <PathContextProvider segment="popTextV2s">
                    <ArrayField
                        items={value.popTextV2s ?? [DEFAULT_POP_TEXT_V2]}
                        onChange={(value) => onFieldChange('popTextV2s', value)}
                        onAdd={() => {
                            const newPopTextV2s = value.popTextV2s
                                ? [...value.popTextV2s, DEFAULT_POP_TEXT_V2]
                                : [DEFAULT_POP_TEXT_V2];
                            onFieldChange('popTextV2s', newPopTextV2s);
                        }}
                    >
                        {(element) => (
                            <ArrayFieldItem key={element.index} {...element}>
                                <PathContextProvider segment={element.index}>
                                    <PopTextV2Input
                                        value={element.item}
                                        onChange={element.onChange}
                                    />
                                </PathContextProvider>
                            </ArrayFieldItem>
                        )}
                    </ArrayField>
                </PathContextProvider>
            </fieldset>
        );
    };

    const renderRegularText = () => {
        return (
            <div style={{ position: 'relative', width: '100%' }}>
                {maxTotalLength !== undefined && (
                    <p>
                        <span className={styles.characterCounterLabel}>
                            Total Combined Length:
                        </span>{' '}
                        <span
                            className={
                                totalLength > maxTotalLength
                                    ? styles.characterCounterError
                                    : ''
                            }
                        >
                            {totalLength}/{maxTotalLength}
                        </span>
                    </p>
                )}
                <fieldset>
                    <legend>Header</legend>
                    <PathContextProvider segment="header">
                        <TextInput
                            value={value.header ?? DEFAULT_TEXT}
                            onChange={(value) => onFieldChange('header', value)}
                        />
                    </PathContextProvider>
                </fieldset>
                <fieldset>
                    <legend>Body</legend>
                    <PathContextProvider segment="body">
                        <TextInput
                            value={value.body ?? DEFAULT_TEXT}
                            onChange={(value) => onFieldChange('body', value)}
                        />
                    </PathContextProvider>
                </fieldset>
            </div>
        );
    };

    const renderInputComponent = (textStyleValue: TextStyle) => {
        switch (textStyleValue) {
            case 'NoText':
                return null;
            case 'RegularText':
                return renderRegularText();
            case 'PopText':
                return renderPopText();
            case 'PopTextV2':
                return renderPopTextV2();
        }
    };

    return (
        <fieldset
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
            }}
        >
            <legend>Text Type</legend>
            <select
                value={textStyle}
                onChange={(e) => onTextStyleChange(e.target.value as TextStyle)}
            >
                <option value="NoText">No Text</option>
                <option value="RegularText">Regular Text</option>
                <option value="PopText">PopText - Legacy</option>
                <option value="PopTextV2">Pop Text V2</option>
            </select>
            {renderInputComponent(textStyle)}
        </fieldset>
    );
};
